import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import {
    ConsumptionWithDisaggregation,
    HouseCharacterization,
    SmartNowConsumptionEvolution,
    SmartNowConsumptionsValues,
    SmartNowEfficiencyEnergetic,
    SmartNowStatus,
} from "@edp-models/smart-now.model";
import { SmartNowService } from "@edp-services/smart-now.service";
import { EContractActions } from "@edp-store/actions/contract.actions";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import {
    ESmartNowActions,
    GetConsumptionEvolution,
    GetConsumptionHistory,
    GetConsumptionPower,
    GetConsumptionPrediction,
    GetConsumptionWithDisaggregation,
    GetContractOptimization,
    GetEfficiencyEnergetic,
    GetHouseCharacterization,
    GetSmartNowStatus,
    SendHouseCharacterization,
    SendNotification,
    SetSmartNowStatus,
    SmartNowAPIError,
} from "@edp-store/actions/smart-now.actions";
import { IAppState } from "@edp-store/state/app.state";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, of } from "rxjs";
import { catchError, exhaustMap, switchMap } from "rxjs/operators";

@Injectable()
export class SmartNowEffects {
    @Effect()
    loadSmartNowStatus$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetSmartNowStatus),
        switchMap((action: GetSmartNowStatus) =>
            this.smartNowService
                .getSmartNowStatus(action.contract.ContractId)
                .pipe(catchError((error: Error) => of(new SmartNowAPIError(error))))
        ),
        switchMap((status: SmartNowStatus | SmartNowAPIError) => {
            if (status instanceof SmartNowAPIError) {
                return of(status);
            } else {
                return of(new SetSmartNowStatus(status));
            }
        })
    );

    @Effect()
    loadHouseCharacterization$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetHouseCharacterization),
        switchMap((action: GetHouseCharacterization) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getHouseCharacterization(action.contract.ContractId).pipe(
                switchMap((houseCharacterization: HouseCharacterization) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetHouseCharacterization, payload: houseCharacterization},
                ]),
                catchError(() => {
                    this.toaster.create(this.translate.instant("pt.edp.edponline.smartnow.defineHouse.service.error"), EdpToastType.error, {
                        showLeftIcon: false,
                        timeout: 3000,
                    }).show();
                    this.router.navigate([`/energia/${action.contract.ContractId}/consumos`]);
                    return [
                        { type: ELayoutActions.ShowLoader, payload: false },
                        { type: ESmartNowActions.SetHouseCharacterization, payload: null}
                    ];
                })
            );
        })
    );

    @Effect()
    sendHouseCharacterization$ = this.actions$.pipe(
        ofType(ESmartNowActions.SendHouseCharacterization),
        switchMap((action: SendHouseCharacterization) =>
            this.smartNowService.sendHouseCharacterization(action.payload).pipe(
                switchMap(() => [{ type: EContractActions.GetContracts }]),
                catchError(() => EMPTY)
            )
        )
    );

    @Effect({ dispatch: false })
    sendNotification$ = this.actions$.pipe(
        ofType(ESmartNowActions.SendNotification),
        switchMap((action: SendNotification) => {
            return this.smartNowService.sendNotification(action.payload).pipe(
                switchMap(() =>
                    this.toaster
                        .create(this.translate.instant("pt.edp.edponline.smartnow.notification.toaster.success"), EdpToastType.info)
                        .show()
                ),
                catchError(() => this.toaster.create(this.translate.instant("pt.edp.edponline.endpoint.error"), EdpToastType.error).show())
            );
        })
    );

    @Effect()
    loadConsumptionWithDesaggregation$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetConsumptionWithDisaggregation),
        switchMap((action: GetConsumptionWithDisaggregation) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getConsumptionWithDesaggregation(action.payload).pipe(
                switchMap((consumptionWithDisaggregation: ConsumptionWithDisaggregation) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionWithDisaggregation, payload: consumptionWithDisaggregation },
                ]),
                catchError(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionWithDisaggregation, payload: null },
                ])
            );
        })
    );

    @Effect()
    getPrediction$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetConsumptionPrediction),
        switchMap((action: GetConsumptionPrediction) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getPrediction(action.contractId).pipe(
                switchMap((prediction: SmartNowConsumptionsValues) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionPrediction, payload: prediction },
                ]),
                catchError(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionPrediction, payload: null },
                ])
            );
        })
    );


    @Effect()
    getEfficiencyEnergetic$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetEfficiencyEnergetic),
        switchMap((action: GetEfficiencyEnergetic) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getEfficiencyEnergetic(action.contractId).pipe(
                switchMap((efficiency: SmartNowEfficiencyEnergetic) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetEfficiencyEnergetic, payload: efficiency },
                ]),
                catchError(() => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetEfficiencyEnergetic, payload: null },
                ])
            );
        })
    );


    @Effect()
    getContractOptimization$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetContractOptimization),
        switchMap((action: GetContractOptimization) => {
            return this.smartNowService.getContractOptimization(action.contractId).pipe(
                switchMap((optimization: SmartNowConsumptionsValues) => [
                    { type: ESmartNowActions.SetContractOptimization, payload: optimization },
                ]),
                catchError(() => EMPTY)
            );
        })
    );

    @Effect()
    getConsumptionPower$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetConsumptionPower),
        exhaustMap((action: GetConsumptionPower) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getConsumptionPower(action.payload).pipe(
                switchMap((consumptionPower: any) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionPower, payload: consumptionPower },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getConsumptionEvolution$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetConsumptionEvolution),
        exhaustMap((action: GetConsumptionEvolution) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getConsumptionEvolution(action.payload).pipe(
                switchMap((consumpttion: SmartNowConsumptionEvolution) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionEvolution, payload: consumpttion },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    @Effect()
    getConsumptionHistory$ = this.actions$.pipe(
        ofType(ESmartNowActions.GetConsumptionHistory),
        exhaustMap((action: GetConsumptionHistory) => {
            this.store.dispatch(new ShowLoader(true));
            return this.smartNowService.getConsumptionHistory(action.payload).pipe(
                switchMap((consumptionHistory: any) => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ESmartNowActions.SetConsumptionHistory, payload: consumptionHistory },
                ]),
                catchError(() => [{ type: ELayoutActions.ShowLoader, payload: false }])
            );
        })
    );

    constructor(
        private actions$: Actions,
        private smartNowService: SmartNowService,
        private store: Store<IAppState>,
        private translate: TranslateService,
        private toaster: EdpToasterService,
        private router: Router
    ) {}
}
